import { Injectable } from '@angular/core';

/**
 * Detects whether a new build has actually been deployed to the server, as opposed to a
 * transient failure (network blip, ad-blocker, etc.), so a failed lazy-chunk load only
 * triggers a reload when it would actually fix something.
 *
 * Comparing chunk filenames referenced by index.html isn't reliable here: only the handful of
 * eagerly-preloaded chunks are listed there, so a deploy that only touches a deeply lazy
 * component (e.g. Signals) wouldn't show up. Instead this fingerprints main.js itself via a
 * fresh HEAD request's ETag/Last-Modified — deployments (see select-distribution.sh) swap in an
 * entirely new site directory, so main.js is always a different file on disk afterwards, even
 * on build configs (rtt/development/localhost) that don't content-hash its filename.
 */
@Injectable({ providedIn: 'root' })
export class DeployVersionService {
  private readonly mainScriptUrl = this.findMainScriptUrl();
  // Captured once, as early as possible (see AppComponent), to reflect the version this page
  // instance actually loaded.
  private readonly initialFingerprint = this.fetchFingerprint();

  private findMainScriptUrl(): string | null {
    const script = Array.from(document.scripts).find((s) =>
      /(^|\/)main[^/]*\.js(\?.*)?$/.test(s.src),
    );
    return script?.src ?? null;
  }

  private async fetchFingerprint(): Promise<string | null> {
    if (!this.mainScriptUrl) return null;
    try {
      const res = await fetch(this.mainScriptUrl, { method: 'HEAD', cache: 'no-store' });
      return res.headers.get('etag') ?? res.headers.get('last-modified') ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns true only if main.js's server-side fingerprint has changed since this page loaded.
   * Returns false (don't reload) whenever that can't be confirmed — no main.js found, a fetch
   * failed, or the server doesn't send ETag/Last-Modified — since none of those are evidence of
   * a new deployment.
   */
  async isNewVersionDeployed(): Promise<boolean> {
    const [initial, latest] = await Promise.all([this.initialFingerprint, this.fetchFingerprint()]);
    return !!initial && !!latest && initial !== latest;
  }
}
